import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, inject,
  DestroyRef} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { IonicModule, InfiniteScrollCustomEvent } from '@ionic/angular';
import { NavigationEnd, Router } from '@angular/router';
import { SidebarService } from '../../../core/services/sidebar.service';
import {
  Subscription,
  Observable,
  tap,
  catchError,
  EMPTY,
  finalize,
  filter,
} from 'rxjs';
import { stringConstant } from 'src/app/core/services/stringConstants';
import { ThemeService } from 'src/app/core/services/theme.service';
import { DeleteDialogComponent } from '../delete-dialog/delete-dialog.component';
import {
  ChatHistoryItem,
  GetUserChatHistoryRequestDto,
  GetUserChatHistoryResponseDto,
} from '../../../core/models/chat-history.dto';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { SharedService } from 'src/app/core/services/shared.service';
import { EffectivePlanService } from 'src/app/firm-workspace/shared/services/effective-plan.service';
import { EffectivePlanView } from 'src/app/firm-workspace/shared/logic/effective-plan-view';
import { UserService } from 'src/app/core/services/user.service';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { DmsAuthService } from 'src/app/dms/services/dms-auth.service';
import { DmsLoaderService } from 'src/app/dms/services/dms-loader.service';
import { LogoutModalComponent } from '../logout-modal/logout-modal.component';
import { FirmContextService } from 'src/app/firm-workspace/shared/services/firm-context.service';
import { WorkspaceService } from 'src/app/firm-workspace/shared/services/workspace.service';
import {
  deriveWorkspaceIndicator,
  WorkspaceIndicator,
} from 'src/app/firm-workspace/shared/logic/workspace-indicator';

interface NavigationItem {
  id: string;
  label: string;
  icon: string;
  route: string;
  isActive?: boolean;
  subLabel?: string;
  // Compact label for the desktop icon rail; full label still used everywhere else
  shortLabel?: string;
  isSvg?: boolean;
  hover?: boolean;
  show?: boolean;
  iconType?: 'icomoon' | 'material';
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    DeleteDialogComponent,
    LogoutModalComponent,
  ],
})
export class SidebarComponent implements OnInit, OnDestroy {
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);
  @ViewChild('deleteDialog') deleteDialog!: DeleteDialogComponent;

  isHovered = false;
  isMobile = false;
  private hoverTimeout: any;
  private leaveTimeout: any;
  hoveredItem: string | null = null;
  currentModule: string | null = null;
  isPinned = false;
  isMobileSidebarOpen = false;
  isDmsExpanded = false;
  isLogoutModalOpen = false;
  private subscription: Subscription = new Subscription();

  public readonly constant: typeof stringConstant;

  // Chat history data from service
  conversations: ChatHistoryItem[] = [];
  isUserLogin!: boolean;
  isLoading: boolean = false;
  error: string | null = null;
  currentPage: number = 1;
  hasMorePages: boolean = true;

  // Logomark source based on theme
  logomarkSrc!: string;

  // Get horizontal logo based on current theme
  get horizontalLogoSrc(): string {
    return this.themeService.isDarkTheme()
      ? this.constant.bharatLawHorizontalDark
      : this.constant.bharatLawHorizontal;
  }

  // Window height for ion-content max-height calculation
  get windowHeight(): number {
    return typeof window !== 'undefined' ? window.innerHeight : 800;
  }

  // Menu state properties
  showMenu: boolean = false;
  menuPosition: { x: number; y: number } = { x: 0, y: 0 };
  selectedConversation: ChatHistoryItem | null = null;

  // Delete dialog properties
  deleteDialogTitle: string = '';
  deleteDialogMessage: string = '';
  /** Workspace-aware plan badge state (pill under the logo). */
  planView: EffectivePlanView | null = null;

  /** Active-workspace state driving the plan badge's org icon and tooltip. */
  workspaceIndicator: WorkspaceIndicator = deriveWorkspaceIndicator(null);

  navigationItems: NavigationItem[] = [
    {
      id: 'home',
      label: 'Home',
      icon: 'search',
      route: '',
      isActive: true,
      isSvg: false,
      hover: true,
      show: true,
    },
    {
      id: 'research-book',
      label: 'Research Book',
      shortLabel: 'Research',
      icon: 'book',
      route: '/research-book',
      isActive: false,
      isSvg: false,
      hover: false,
      show: true,
    },
    {
      id: 'data-explorer',
      label: 'Data Explorer',
      shortLabel: 'Explorer',
      icon: 'explore',
      route: '/data-explorer',
      isActive: false,
      isSvg: false,
      hover: false,
      show: true,
    },
    {
      id: 'pm-cases',
      label: 'Case Management',
      shortLabel: 'Cases',
      icon: 'law',
      route: '/pm/cases',
      isActive: false,
      isSvg: false,
      hover: false,
      show: true,
    },
    {
      id: 'dms',
      label: 'Document Management',
      shortLabel: 'Docs',
      icon: 'folder',
      route: '/dms',
      isActive: true,
      isSvg: false,
      hover: false,
      show: true,
    },
    {
      id: 'firm-workspace',
      label: 'Team',
      icon: 'groups',
      route: '/firm',
      isActive: false,
      isSvg: false,
      hover: false,
      show: true,
      iconType: 'material',
    },
    {
      id: 'pm-calendar',
      label: 'Calendar',
      icon: 'calendar_month',
      route: '/calendar',
      isActive: false,
      isSvg: false,
      hover: false,
      show: false,
      iconType: 'material',
    },
  ];
  // DMS sub items
  dmsSubItems: NavigationItem[] = [
    
    {
      id: 'dms-cases',
      label: 'All Cases',
      icon: 'list_alt',
      route: '/dms/cases',
      show: true,
    },
    {
      id: 'dms-documents',
      label: 'Documents',
      icon: 'description',
      route: '/dms/documents',
      show: true,
    },
    
  ];

  accountItems: NavigationItem[] = [
    {
      id: 'profile',
      label: 'Profile',
      icon: 'profile',
      route: '/profile',
      isActive: false,
      isSvg: false,
      hover: true,
      show: true,
    },
    {
      id: 'login',
      label: 'Login',
      icon: 'logout',
      route: '',
      isActive: false,
      isSvg: false,
      hover: false,
      show: true,
    },
    {
      id: 'logout',
      label: 'Logout',
      icon: 'logout',
      route: '',
      isActive: false,
      isSvg: false,
      hover: false,
      show: true,
    },
  ];

  /**
   * Constructor for the SidebarComponent
   */
  constructor(
    private router: Router,
    private sidebarService: SidebarService,
    private authenticationService: AuthenticationService,
    private sharedService: SharedService,
    private effectivePlanService: EffectivePlanService,
    private featureFlagService: FeatureFlagService,
    private themeService: ThemeService,
    public dmsAuth: DmsAuthService,
    private dmsLoaderService: DmsLoaderService,
    private breakpointObserver: BreakpointObserver,
    public firmContextService: FirmContextService,
    public workspaceInitService: WorkspaceService,
    private userService: UserService,
  ) {
    this.constant = stringConstant;

    // Initialize logomark based on current theme
    this.logomarkSrc = this.themeService.isDarkTheme()
      ? this.constant.bharatLawLogoMarkDark
      : this.constant.bharatLawLogoMark;

    this.sharedService.getUserLoginStatus().pipe(tap(() => this._cdr.markForCheck())).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (res) => {
        this.isUserLogin = res;

        if (this.isUserLogin) {
          this.getSubscriptionPlan();
          this.loadChatHistory();
          this.workspaceInitService.initialize();
          this.loadUserAvatar();
        }
      },
    });

    // Keep the workspace indicator in sync with the active firm.
    this.firmContextService.activeFirm$
      .pipe(tap(() => this._cdr.markForCheck()))
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((firm) => (this.workspaceIndicator = deriveWorkspaceIndicator(firm)));
  }

  // Profile avatar state (initials + optional picture) for the sidebar profile item.
  userInitial = '';
  userProfilePicUrl: string | null = null;
  avatarFailed = false;

  private loadUserAvatar(): void {
    // Use a cached picture immediately if present, then refresh from the API.
    this.userProfilePicUrl = this.sharedService.getLocalStorage('profilePictureUrl') || null;
    this.userService.getCurrentUser().pipe(tap(() => this._cdr.markForCheck())).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (user) => {
        const first = (user.firstName || '').trim();
        this.userInitial = (first || user.email || '?').charAt(0).toUpperCase();
        if (user.profilePictureUrl) {
          this.userProfilePicUrl = user.profilePictureUrl;
          this.sharedService.setLocalStorage('profilePictureUrl', user.profilePictureUrl);
        }
      },
      error: () => { /* keep cached/fallback avatar */ },
    });
  }

  onAvatarError(): void {
    this.avatarFailed = true;
    this.userProfilePicUrl = null;
  }

  /**
   * Initializes the sidebar component
   */
  ngOnInit() {
    // Set active item based on current route
    this.updateActiveItem();

    // Subscribe to router events to update active item on navigation (including page refresh)
    this.subscription.add(
      this.router.events
        .pipe(
          filter(
            (event): event is NavigationEnd => event instanceof NavigationEnd,
          ),
        )
        .pipe(tap(() => this._cdr.markForCheck())).pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => this.updateActiveItem()),
    );

    // Initialize logomark based on current theme
    this.updateLogomark();

    // Subscribe to theme changes
    this.subscription.add(
      this.themeService.theme$.pipe(tap(() => this._cdr.markForCheck())).pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
        this.updateLogomark();
      }),
    );

    // Subscribe to sidebar toggle events
    this.subscription.add(
      this.sidebarService.sidebarToggle$.pipe(tap(() => this._cdr.markForCheck())).pipe(takeUntilDestroyed(this._destroyRef)).subscribe((isOpen) => {
        this.isMobileSidebarOpen = isOpen;
      }),
    );

    // Subscribe to chat history updates
    this.subscription.add(
      this.sidebarService.chatHistory$.pipe(tap(() => this._cdr.markForCheck())).pipe(takeUntilDestroyed(this._destroyRef)).subscribe((chats) => {
        this.conversations = chats;
      }),
    );

    // Subscribe to chat created events - refresh chat history when a new chat is created
    this.subscription.add(
      this.sidebarService.chatCreated$.pipe(tap(() => this._cdr.markForCheck())).pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
        // Reload chat history when a new chat is created
        // Always try to load - the API will handle authentication
        this.loadChatHistory();
      }),
    );

    // Subscribe to feature flags for Data Explorer and Research Book
    // Use BehaviorSubject directly for immediate value and updates
    // Subscribe to feature flags for Data Explorer
    this.subscription.add(
      this.featureFlagService.dataExplorerFlag.pipe(tap(() => this._cdr.markForCheck())).pipe(takeUntilDestroyed(this._destroyRef)).subscribe((isEnabled) => {
        this.navigationItems.find(
          (item) => item.id === this.constant.dataExplorerId,
        )!.show = isEnabled;
      }),
    );

    // Subscribe to feature flags for Research Book
    this.subscription.add(
      this.featureFlagService.researchBookFlag.pipe(tap(() => this._cdr.markForCheck())).pipe(takeUntilDestroyed(this._destroyRef)).subscribe((isEnabled) => {
        this.navigationItems.find(
          (item) => item.id === this.constant.researchBookId,
        )!.show = isEnabled;
      }),
    );

    // Subscribe to feature flag for Document Management (DMS)
    this.subscription.add(
      this.featureFlagService.getDmsFeatureFlag().pipe(tap(() => this._cdr.markForCheck())).pipe(takeUntilDestroyed(this._destroyRef)).subscribe((isEnabled) => {
        const dmsItem = this.navigationItems.find((item) => item.id === this.constant.dmsLabel);
        if (dmsItem) {
          dmsItem.show = isEnabled;
        }
      }),
    );

    // Subscribe to feature flag for Calendar (Practice Management)
    this.subscription.add(
      this.featureFlagService.getCalendarFeatureFlag().pipe(tap(() => this._cdr.markForCheck())).pipe(takeUntilDestroyed(this._destroyRef)).subscribe((isEnabled) => {
        const calendarItem = this.navigationItems.find((item) => item.id === this.constant.pmCalendarId);
        if (calendarItem) {
          calendarItem.show = isEnabled;
        }
      }),
    );

    // Initial fetch for DMS user to get roles
    this.dmsAuth.fetchUser();

    // Subscribe to screen size changes
    this.subscription.add(
      this.breakpointObserver
        .observe([Breakpoints.Handset, '(max-width: 767px)'])
        .pipe(tap(() => this._cdr.markForCheck())).pipe(takeUntilDestroyed(this._destroyRef)).subscribe(result => {
          this.isMobile = result.matches;
        })
    );

    // Add click outside listener to close menu
    this.addClickOutsideListener();
  }

  /**
   * Unsubscribes from all subscriptions and clears any pending timeouts
   */
  ngOnDestroy() {
    this.subscription?.unsubscribe();

    // Clear any pending timeouts
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
    if (this.leaveTimeout) {
      clearTimeout(this.leaveTimeout);
    }
  }

  /**
   * Navigates to the home page and clears the search text
   */
  onAddClick() {
    // Navigate to home page and clear the search text
    this.router.navigate(['/']);
    this.sidebarService.clearSearch();

    // Close mobile sidebar after navigation (only on mobile)
    if (this.isMobile) {
      this.sidebarService.toggleSidebar();
    }
  }

  /**
   * Navigates to the search results page with the conversation title as query
   * @param conversation - The conversation to navigate to
   */
  onConversationClick(conversation: ChatHistoryItem) {
    // Navigate to search results page with the conversation title as query
    this.router.navigate([this.constant.searchPageRoute], {
      queryParams: { q: conversation.chatName, chatId: conversation.id },
    });

    // Close mobile sidebar after navigation (only on mobile)
    if (this.isMobile) {
      this.sidebarService.toggleSidebar();
    }
  }

  /**
   * Loads the user's chat history from the API.
   */
  loadChatHistory(): void {
    this.isLoading = true;
    this.error = null;
    this.currentPage = 1;
    this.hasMorePages = true;

    // Default pagination parameters
    const requestDto: GetUserChatHistoryRequestDto = {
      page: 1,
      pageSize: 20,
    };

    // Call the API: api/legal-query/user-chats-history?page=1&pageSize=20
    this.sidebarService.getUserChatHistory(requestDto).pipe(tap(() => this._cdr.markForCheck())).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (response: GetUserChatHistoryResponseDto) => {
        this.conversations = response.chats;
        this.sidebarService.setChatHistory(response.chats);
        this.currentPage = response.currentPage;
        this.hasMorePages = response.hasNextPage;
        this.isLoading = false;
      },
      error: (error) => {
        this.error = this.constant.failedToLoadChatHistory;
        this.isLoading = false;
        // Log error for debugging
      },
    });
  }

  /**
   * Loads more chats for pagination
   * @param page - The page number to load (optional, defaults to currentPage + 1)
   * @returns Observable that emits the response when loading is complete
   */
  loadMoreChats(page?: number): Observable<GetUserChatHistoryResponseDto> {
    if (this.isLoading || !this.hasMorePages) {
      // Return an empty observable if conditions aren't met
      return EMPTY;
    }

    this.isLoading = true;
    this.error = null;

    const pageToLoad = page ?? this.currentPage + 1;
    const requestDto: GetUserChatHistoryRequestDto = {
      page: pageToLoad,
      pageSize: 20,
    };

    return this.sidebarService.getUserChatHistory(requestDto).pipe(
      tap((response: GetUserChatHistoryResponseDto) => {
        // Load more - append to existing list
        this.conversations = [...this.conversations, ...response.chats];
        this.sidebarService.setChatHistory(this.conversations);
        this.currentPage = response.currentPage;
        this.hasMorePages = response.hasNextPage;
        this.isLoading = false;
      }),
      catchError((error) => {
        this.error = this.constant.failedToLoadChatHistory;
        this.isLoading = false;
        // Re-throw error so subscribers can handle it
        throw error;
      }),
    );
  }

  /**
   * Handles the infinite scroll event from Ionic
   * This method is required because Ionic's ion-infinite-scroll requires
   * calling event.target.complete() to signal that loading is done.
   * @param event - The infinite scroll event from Ionic
   */
  onInfiniteScroll(event: InfiniteScrollCustomEvent): void {
    // Early return if no more pages or already loading
    if (!this.hasMorePages || this.isLoading) {
      event.target.complete();
      return;
    }

    // Load more chats and complete the event when done (success or error)
    this.loadMoreChats()
      .pipe(
        finalize(() => {
          // Always complete the infinite scroll event, even on error
          event.target.complete();
        }),
      )
      .pipe(tap(() => this._cdr.markForCheck())).pipe(takeUntilDestroyed(this._destroyRef)).subscribe();
  }

  /**
   * Navigates to the home page and clears the search text
   */
  onNewConversationClick() {
    // Navigate to home page and clear the search text
    this.router.navigate(['/']);
    this.sidebarService.clearSearch();

    // Close mobile sidebar after navigation (only on mobile)
    if (this.isMobile) {
      this.sidebarService.toggleSidebar();
    }
  }

  /**
   * Opens the conversation menu
   * @param conversation - The conversation to open the menu for
   * @param event - The event that triggered the menu
   */
  onConversationMenuClick(conversation: ChatHistoryItem, event: Event) {
    event.stopPropagation();

    // Close any existing menu first
    this.hideMenu();

    // Get the position of the clicked element
    const rect = (event.target as HTMLElement).getBoundingClientRect();

    // Set menu position
    this.menuPosition = {
      x: rect.left,
      y: rect.bottom + 4,
    };

    // Set selected conversation and show menu
    this.selectedConversation = conversation;
    this.showMenu = true;
  }

  /**
   * Hides the conversation menu
   */
  hideMenu() {
    this.showMenu = false;
    this.selectedConversation = null;
  }

  /**
   * Deletes the selected conversation
   */
  onDeleteConversation() {
    if (this.selectedConversation) {
      // Set dialog properties
      this.deleteDialogTitle = 'Delete Conversation';
      this.deleteDialogMessage = `Are you sure you want to delete "${this.selectedConversation.chatName}"? This action cannot be undone.`;

      // Show dialog by setting isOpen to true
      this.deleteDialog.isOpen = true;

      // Don't hide menu yet - keep selectedConversation for the delete confirmation
    }
  }

  /**
   * Confirms the deletion of the selected conversation
   */
  onDeleteConfirmed() {
    if (this.selectedConversation) {
      // Remove from conversations array
      const index = this.conversations.findIndex(
        (conv) => conv.id === this.selectedConversation!.id,
      );
      if (index > -1) {
        this.conversations.splice(index, 1);
        this.sidebarService.setChatHistory(this.conversations);
      }

      // Clear the selected conversation and hide menu
      this.selectedConversation = null;
      this.hideMenu();

      // Close the sidebar after deletion
      this.closeSidebar();
    }
  }

  /**
   * Handles when the delete dialog is cancelled or dismissed
   */
  onDeleteCancelled() {
    // Hide menu when delete is cancelled or dismissed
    this.hideMenu();
  }

  /**
   * Closes the sidebar
   */
  private closeSidebar(): void {
    // Close mobile sidebar if open
    if (this.isMobileSidebarOpen) {
      this.sidebarService.toggleSidebar();
    }

    // Close expanded panel if pinned
    if (this.isPinned) {
      this.isPinned = false;
      this.isHovered = false;
      this.hoveredItem = null;
      this.sidebarService.setSidebarPinned(false);
    }
  }

  /**
   * Adds a click outside listener to the sidebar
   */
  private addClickOutsideListener(): void {
    const clickHandler = (event: Event) => {
      const target = event.target as HTMLElement;
      const isMenuClick = target.closest('.conversation-menu');
      const isMenuButtonClick = target.closest('[data-menu-trigger]');

      if (!isMenuClick && !isMenuButtonClick && this.showMenu) {
        this.hideMenu();
      }
    };

    document.addEventListener('click', clickHandler);

    // Store the cleanup function
    this.subscription.add(() => {
      document.removeEventListener('click', clickHandler);
    });
  }

  /**
   * Navigates to the given item
   * @param item - The item to navigate to
   */
  navigateTo(item: NavigationItem): void {
    // Clear any pending hover/leave timeouts to prevent them from
    // overwriting the state we're about to set via this click
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
    if (this.leaveTimeout) {
      clearTimeout(this.leaveTimeout);
      this.leaveTimeout = null;
    }

    if (item.id === this.constant.dmsLabel) {
      if (this.isMobile) {
        this.isDmsExpanded = !this.isDmsExpanded;
      } else {
        // On desktop, navigate to DMS cases and pin if not already in DMS
        // Only show loader when switching INTO DMS from a non-DMS page.
        // If already in DMS, router.navigate(['/dms']) resolves to the same URL and
        // Angular ignores it — no NavigationEnd fires, leaving the loader stuck.
        if (!this.router.url.startsWith('/dms')) {
          this.dmsLoaderService.mainLoaderOn('Switching to ' + item.label + '...');
        }
        this.router.navigate(['/dms/cases']);
        this.isPinned = true;
        this.hoveredItem = this.constant.dmsLabel;
        this.isHovered = true;

        // Set 'All Cases' sub-item as active immediately
        this.dmsSubItems.forEach((sub) => {
          sub.isActive = sub.id === 'dms-cases';
        });
      }
      return;
    }

    // Update active state
    this.navigationItems.forEach((navItem) => (navItem.isActive = false));
    this.dmsSubItems.forEach((subItem) => (subItem.isActive = false));
    this.accountItems.forEach((accountItem) => (accountItem.isActive = false));

    item.isActive = true;

    // Navigate to route
    this.router.navigate([item.route]);

    // Close mobile sidebar after navigation (only on mobile)
    if (this.isMobile) {
      this.sidebarService.toggleSidebar();
    }
  }

  /**
   * Updates the active item based on the current route
   */
  /**
   * Update logomark based on current theme
   */
  private updateLogomark(): void {
    this.logomarkSrc = this.themeService.isDarkTheme()
      ? this.constant.bharatLawLogoMarkDark
      : this.constant.bharatLawLogoMark;
  }

  private updateActiveItem(): void {
    const urlTree = this.router.parseUrl(this.router.url);
    urlTree.queryParams = {};
    urlTree.fragment = null;
    const currentRoute = urlTree.toString();

    // Check navigation items
    this.navigationItems.forEach((item) => {
      // Handle home route
      if (item.route === '') {
        item.isActive = currentRoute === '/' || currentRoute === '';

      } else if (
        item.route.startsWith('/pm/') &&
        currentRoute.startsWith('/pm/')
      ) {
        // Match the exact PM section (e.g. /pm/cases vs /pm/clients)
        item.isActive =
          currentRoute === item.route ||
          currentRoute.startsWith(item.route + '/');

      } else if (
        item.route.startsWith('/dms') &&
        currentRoute.startsWith('/dms')
      ) {
        // Document Management
        item.isActive = true;

      } else {
        item.isActive =
          currentRoute === item.route ||
          currentRoute.startsWith(item.route + '/');
      }
    });

    if (!currentRoute.startsWith('/dms')) {
      // If we leave DMS, we should reset the module and unpin
      this.currentModule = null;
      this.isPinned = false;
      this.isHovered = false;
      this.hoveredItem = null;
    } else {
      this.currentModule = this.constant.dmsLabel;
      // On big screens, keep DMS sidebar pinned by default
      if (!this.isMobile) {
        this.isPinned = true;
      }
    }

    // Check DMS sub-items
    this.dmsSubItems.forEach((subItem) => {
      subItem.isActive =
        currentRoute === subItem.route ||
        currentRoute.startsWith(subItem.route + '/');
    });

    // We can also determine if a research book sub-item should be marked active if needed, 
    // but typically they act as anchor links within the detail page, so we don't strictly keep them "active" 
    // unless we also listen to scroll events (which is handled by research-book-detail's activeSection).
    
    // If no sub-item matches but we are in DMS, default to 'All Cases'
    if (currentRoute.startsWith('/dms') && !this.dmsSubItems.some((s) => s.isActive)) {
      const casesItem = this.dmsSubItems.find((s) => s.id === 'dms-cases');
      if (casesItem) casesItem.isActive = true;
    }

    // If any DMS sub-item is active, keep DMS expanded
    if (this.dmsSubItems.some((s) => s.isActive)) {
      this.isDmsExpanded = true;
    }

    // Check account items
    this.accountItems.forEach((item) => {
      if (item.id === 'profile') {
        item.isActive =
          currentRoute === '/profile' || currentRoute === '/subscription';
      } else {
        item.isActive = currentRoute === item.route;
      }
    });
  }

  /**
   * Handles the hover event for the sidebar
   * @param itemId - The id of the item that is being hovered over
   */
  onSidebarHover(itemId: string): void {
    // Don't change hover state if menu is open
    if (this.showMenu) {
      return;
    }

    // Clear any existing timeouts
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
    if (this.leaveTimeout) {
      clearTimeout(this.leaveTimeout);
    }

    // Only update if the item is different or not already hovered
    if (this.hoveredItem !== itemId || !this.isHovered) {
      this.hoverTimeout = setTimeout(() => {
        this.isHovered = true;
        this.hoveredItem = itemId;
      }, 50); // Small delay to prevent rapid changes
    }
  }

  /**
   * Handles the leave event for the sidebar
   */
  onSidebarLeave(): void {
    // Don't change hover state if menu is open
    if (this.showMenu) {
      return;
    }

    // Clear hover timeout
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }

    this.leaveTimeout = setTimeout(() => {
      if (!this.isPinned) {
        this.isHovered = false;
      }
      this.hoveredItem = null;
    }, 150); // Responsive delay
  }

  /**
   * Handles the enter event for the expanded panel
   */
  onExpandedPanelEnter(): void {
    // Don't change hover state if menu is open
    if (this.showMenu) {
      return;
    }

    // Clear any leave timeout when entering the expanded panel
    if (this.leaveTimeout) {
      clearTimeout(this.leaveTimeout);
    }
    // Keep the panel open when hovering over it
    if (this.hoveredItem && !this.isPinned) {
      this.isHovered = true;
    }
  }

  /**
   * Handles the leave event for the expanded panel
   */
  onExpandedPanelLeave(): void {
    // Don't change hover state if menu is open
    if (this.showMenu) {
      return;
    }

    // When leaving the expanded panel, start the hide timeout
    this.leaveTimeout = setTimeout(() => {
      if (!this.isPinned) {
        this.isHovered = false;
      }
      this.hoveredItem = null;
    }, 100); // Short delay when leaving expanded panel
  }

  /**
   * Handles the pin click event for the sidebar
   */
  onPinClick(): void {
    this.isPinned = !this.isPinned;
    this.sidebarService.setSidebarPinned(this.isPinned);
    if (this.isPinned) {
      // When pinning, ensure the sidebar is shown
      this.isHovered = true;
    } else {
      // When unpinning, hide the sidebar
      this.isHovered = false;
      this.hoveredItem = null;
    }
  }

  /**
   * Handles the backdrop click event for the sidebar
   * @param event - The event that triggered the backdrop click
   */
  onBackdropClick(event: Event): void {
    // Only close sidebar on mobile when clicking the backdrop
    // Don't close if clicking on conversation menu
    const target = event.target as HTMLElement;
    if (target && target.closest('.conversation-menu')) {
      return;
    }

    if (this.isMobile && this.isMobileSidebarOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  /**
   * Closes the mobile sidebar
   */
  closeMobileSidebar(): void {
    if (this.isMobile) {
      this.sidebarService.toggleSidebar();
    }
  }

  /**
   * Gets the label of the hovered item
   * @returns the label of the hovered item
   */
  getHoveredLabel(): string {
    // If hovering a specific item, always show that item's label
    if (this.hoveredItem) {
      if (this.hoveredItem === this.constant.dmsLabel) {
        return this.constant.documentManagementLabel;
      }
      const allItems = [...this.navigationItems, ...this.accountItems];
      const matched = allItems.find((item) => item.id === this.hoveredItem);
      if (matched?.label) {
        return matched.label;
      }
    }

    // No hover: use pinned module label or default to Home
    if (this.isPinned && this.currentModule === this.constant.dmsLabel) {
      return this.constant.documentManagementLabel;
    }

    if (this.isPinned) {
      const allItems = [...this.navigationItems, ...this.accountItems];
      const matched = allItems.find((item) => item.id === this.constant.homeLabel);
      if (matched?.label) {
        return matched.label;
      }
    }

    return '';
  }

  /** Icons that use Material Symbols (not in custom icomoon font) */
  private readonly materialIconMap: Record<string, string> = {
    explore: 'explore',
    page_info: 'info',
    profile: 'person',
    logout: 'logout',
    login: 'login',
    folder: 'folder',
    groups: 'groups',
    person: 'person',
    calendar_month: 'calendar_month',
  };

  /**
   * Returns the Material Symbol name for icons not in the custom font, or null to use icon-* class.
   */
  getMaterialIconName(icon: string): string | null {
    return this.materialIconMap[icon] ?? null;
  }

  /**
   * Gets the name of the icon
   * @param materialIcon - The material icon to get the name of
   * @returns the name of the icon
   */
  getIconName(materialIcon: string): string {
    const iconMap: { [key: string]: string } = {
      search: 'search',
      book: 'book',
      explore: 'explore',
      profile: 'profile',
      logout: 'logout',
      login: 'login',
      folder: 'folder',
    };
    return iconMap[materialIcon] || 'help-outline';
  }

  /**
   * Handles user authentication actions (signup, login, logout)
   * Navigates user to the authentication page
   */
  onAuthenticationClick(): void {
    if (this.isUserLogin) {
      this.logout();
    } else {
      this.login();
    }
  }
  /** Subscriptio button
   */

  onClickSubscription() {
    this.router.navigate([this.constant.subscriptionUrl])
  }


  switchToOrganizationFirm(): void {
    this.workspaceInitService.switchToOrganizationFirm();
    this.router.navigate(['/firm']);
  }

  createNewWorkspace(): void {
    this.router.navigate(['/onboarding/create-workspace']);
  }

  /**
   * Logout the user.
   */
  logout(): void {
    this.isLogoutModalOpen = true;
  }

  onLogoutConfirm(): void {
    this.isLogoutModalOpen = false;
    this.authenticationService.auth0Logout();
  }

  onLogoutCancel(): void {
    this.isLogoutModalOpen = false;
  }

  /**
   * Login the user using auth0
   */
  login(): void {
    this.authenticationService.auth0Logout(false);
    this.authenticationService.auth0Login(this.constant.login);
  }

  /**
   * Subscribes the badge to the effective plan of the ACTIVE workspace
   * (per-user plan in individual context, Ultimate seat in org context).
   */
  getSubscriptionPlan(): void {
    this.effectivePlanService.planView$
      .pipe(tap(() => this._cdr.markForCheck()))
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((view) => (this.planView = view));
  }

  /**
   * Checks if a DMS navigation item should be shown based on user role
   * @param itemId - The id of the DMS item
   * @returns true if the item should be shown
   */
  shouldShowDmsItem(itemId: string): boolean {
    // DMS destinations are authenticated-only — hide them for logged-out users
    // (e.g. on the /dms/welcome landing).
    if (!this.isUserLogin) {
      return false;
    }

    const firmRole = this.firmContextService.activeFirm?.myRole;

    if (itemId === this.constant.dmsClientsLabel) {
      // Any firm user can view clients.
      return firmRole !== undefined && firmRole !== null;
    }
    return true;
  }
}
